import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { useKeycloak } from '@/shared/composables/useKeycloak'
import type { AccessTier } from '§§C/types/access-tier.type'

type UserPermissions = {
  global: string[]
  client: Record<string, string[]>
  project: Record<string, string[]>
  concept: Record<string, string[]>
}

type UserRole = {
  uuid: string
  title: string
  common_id: string
  description?: string
  is_system_role: boolean
  context_scope: string
}

export const useAuthStore = defineStore('auth', () => {
  // State
  const permissions = ref<UserPermissions>({
    global: [],
    client: {},
    project: {},
    concept: {}
  })
  const userRoles = ref<UserRole[]>([])
  const permissionsLoading = ref(false)
  const permissionsError = ref<string | null>(null)
  const permissionsLoaded = ref(false)
  const organizationPermissions = ref<string[]>([])
  const organizationPermissionsLoaded = ref(false)
  // Stable, permission-derived access classification for the chosen org. 'none'
  // until an org is selected (no org scope ⇒ no org permissions).
  const accessTier = ref<AccessTier>('none')

  // Monotonic counter used by access-control.service to discard stale permission
  // responses. Without this, two concurrent fetches (e.g. unscoped init + org-scoped
  // refresh in the router guard, or rapid org switches) can race and let the slower
  // response overwrite the newer state.
  const permissionsRequestSeq = ref(0)

  // Get current user from Keycloak
  const { userProfile } = useKeycloak()

  // Getters
  const currentUserId = computed(() => (userProfile.value as any)?.userid || null)
  const currentUserEmail = computed(() => userProfile.value?.email || null)
  const currentUserName = computed(
    () => (userProfile.value as any)?.name || userProfile.value?.preferred_username || null
  )

  // Check if user has a specific permission
  const hasPermission = computed(
    () =>
      (resource: string, action: string): boolean => {
        const permissionString = `${resource}:${action}`

        // Check global permissions first
        if (permissions.value.global.includes(permissionString)) {
          return true
        }

        // Check mainapp client permissions
        const mainappPermissions = permissions.value.client.mainapp || []
        if (mainappPermissions.includes(permissionString)) {
          return true
        }

        // Check organization-scoped permissions
        if (organizationPermissions.value.includes(permissionString)) {
          return true
        }

        return false
      }
  )

  // Check if user has read permission for a resource
  const canRead = computed(
    () =>
      (resource: string): boolean => {
        return hasPermission.value(resource, 'read')
      }
  )

  // Check if user has write permission for a resource
  const canWrite = computed(
    () =>
      (resource: string): boolean => {
        return hasPermission.value(resource, 'write')
      }
  )

  // Check if user has create permission for a resource
  const canCreate = computed(
    () =>
      (resource: string): boolean => {
        return hasPermission.value(resource, 'create')
      }
  )

  // Check if user has delete permission for a resource
  const canDelete = computed(
    () =>
      (resource: string): boolean => {
        return hasPermission.value(resource, 'delete')
      }
  )

  // Check if user has any permissions at all
  const hasAnyPermissions = computed(() => {
    return (
      permissions.value.global.length > 0 ||
      Object.values(permissions.value.client).some((perms) => perms.length > 0)
    )
  })

  // Check if user has access to the mainapp application
  // User must have 'client-access' role in the resource_access for the Keycloak client
  const hasClientAccess = computed(() => {
    const clientId = import.meta.env.VITE_KEYCLOAK_CLIENT_ID
    if (!clientId) {
      console.error('VITE_KEYCLOAK_CLIENT_ID is not configured')
      return false
    }

    const resourceAccess = (userProfile.value as any)?.resource_access
    if (!resourceAccess) {
      return false
    }

    const clientAccess = resourceAccess[clientId]
    if (!clientAccess?.roles) {
      return false
    }

    return clientAccess.roles.includes('client-access')
  })

  // Get all resources user has read access to
  const readableResources = computed(() => {
    const resources = new Set<string>()

    // Check global permissions
    permissions.value.global.forEach((perm) => {
      const [resource, action] = perm.split(':')
      if (action === 'read') {
        resources.add(resource)
      }
    })

    // Check mainapp client permissions
    const mainappPermissions = permissions.value.client.mainapp || []
    mainappPermissions.forEach((perm) => {
      const [resource, action] = perm.split(':')
      if (action === 'read') {
        resources.add(resource)
      }
    })

    return Array.from(resources)
  })

  // Actions
  const setPermissions = (userPermissions: UserPermissions): void => {
    permissions.value = userPermissions
    permissionsLoaded.value = true
  }

  const setUserPermissions = (globalPermissions: string[]): void => {
    permissions.value.global = globalPermissions
    permissionsLoaded.value = true
  }

  const setUserRoles = (roles: UserRole[]): void => {
    userRoles.value = roles
  }

  const setPermissionsLoading = (loading: boolean): void => {
    permissionsLoading.value = loading
  }

  const setPermissionsError = (error: string | null): void => {
    permissionsError.value = error
  }

  const setOrganizationPermissions = (perms: string[]): void => {
    organizationPermissions.value = perms
    organizationPermissionsLoaded.value = true
  }

  const setAccessTier = (tier: AccessTier): void => {
    accessTier.value = tier
  }

  const nextPermissionsRequestSeq = (): number => {
    permissionsRequestSeq.value += 1
    return permissionsRequestSeq.value
  }

  const isLatestPermissionsRequest = (seq: number): boolean => {
    return seq === permissionsRequestSeq.value
  }

  // Admin access is the top rung of the backend-derived access_tier — the single
  // source of truth for "how much access does this user have". The backend derives
  // 'admin' from exactly the same organization_member:* / organization_access:*
  // permissions this used to check directly, so behaviour is unchanged; reading it
  // from access_tier keeps the admin gate, the access badge, and every other
  // consumer from drifting apart.
  const isOrganizationAdmin = computed(() => accessTier.value === 'admin')

  // The granular "what exactly can this admin manage" distinctions can't be
  // expressed by access_tier (it collapses both into 'admin'), so they stay
  // derived from the raw organization permissions.
  const canManageOrgMembers = computed(() => {
    return organizationPermissions.value.some((p) => p.startsWith('organization_member:'))
  })

  const canManageOrgAccess = computed(() => {
    return organizationPermissions.value.some((p) => p.startsWith('organization_access:'))
  })

  const hasOrganizationPermissions = computed(() => organizationPermissions.value.length > 0)

  const clearPermissions = (): void => {
    permissions.value = {
      global: [],
      client: {},
      project: {},
      concept: {}
    }
    userRoles.value = []
    permissionsLoaded.value = false
    permissionsError.value = null
    organizationPermissions.value = []
    organizationPermissionsLoaded.value = false
    accessTier.value = 'none'
    permissionsRequestSeq.value += 1
  }

  return {
    // State
    permissions,
    userRoles,
    permissionsLoading,
    permissionsError,
    permissionsLoaded,
    // Getters
    currentUserId,
    currentUserEmail,
    currentUserName,
    hasPermission,
    canRead,
    canWrite,
    canCreate,
    canDelete,
    hasAnyPermissions,
    hasClientAccess,
    readableResources,
    isOrganizationAdmin,
    canManageOrgMembers,
    canManageOrgAccess,
    hasOrganizationPermissions,
    organizationPermissionsLoaded,
    accessTier,
    // Actions
    setPermissions,
    setUserPermissions,
    setUserRoles,
    setPermissionsLoading,
    setPermissionsError,
    setOrganizationPermissions,
    setAccessTier,
    nextPermissionsRequestSeq,
    isLatestPermissionsRequest,
    clearPermissions
  }
})
